import { z } from 'zod'
import { isIsolatedPreviewBuild } from './runtime-build'

/**
 * Centralised environment-variable validator.
 *
 * Defensive parsing. Never throw at module load — that
 * crashes the React Native bridge on Android (NewArch + Hermes →
 * blank/gray screen with destroyed React Activity, Expo issue
 * #41543), which prevents `expo-updates` from applying an OTA fix
 * because the JS crashes BEFORE the OTA download can complete.
 *
 * The previous design threw a clear error so misconfigurations were
 * caught at QA. The trade-off: a misconfigured production AAB
 * (Carlos's case — EAS production env was missing
 * EXPO_PUBLIC_SUPABASE_URL/_ANON_KEY/_MAPBOX_TOKEN/etc.) became
 * unrecoverable on Android because the bridge died before
 * expo-updates could rescue. The new design exposes
 * `env.isMisconfigured` and a `configError` string so the root
 * layout can render a clean fallback screen while the OTA downloads
 * — graceful degradation without losing the QA signal (the screen
 * shows the missing-vars list).
 *
 * Two severity tiers (preserved):
 *   - alwaysRequired: missing → isMisconfigured = true with reason
 *   - productionRequired: missing in prod → isMisconfigured = true
 *
 * Why placeholder values: services like Supabase need a "URL" to
 * pass their own URL parser at module-load. The placeholder
 * (`https://invalid.supabase.co`) is intentionally INVALID so any
 * unintended call from a misconfigured app fails LOUDLY (DNS error)
 * rather than silently leaking to a wrong endpoint.
 */

const nonEmpty = z.string().min(1, 'required')
const urlString = z.string().url('must be a valid URL')

const schema = z.object({
  // Always required — app cannot function without these
  EXPO_PUBLIC_SUPABASE_URL: urlString,
  EXPO_PUBLIC_SUPABASE_ANON_KEY: nonEmpty,

  // Environment tier
  EXPO_PUBLIC_ENV: z
    .enum(['development', 'preview', 'production', 'staging'])
    .default('development'),

  // Required in production only — validated separately below
  EXPO_PUBLIC_SENTRY_DSN: z.string().optional(),
  EXPO_PUBLIC_MAPBOX_TOKEN: z.string().optional(),
  EXPO_PUBLIC_MAPBOX_STYLE_URL: z.string().optional(),
  EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID: z.string().optional(),
  EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID: z.string().optional(),
  EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID: z.string().optional(),
  EXPO_PUBLIC_IOS_APP_STORE_URL: z.string().url().optional(),

  // Feature flags + non-sensitive config
  EXPO_PUBLIC_PHONE_AUTH_ENABLED: z.enum(['true', 'false']).default('true'),
  EXPO_PUBLIC_PRODUCT_TELEMETRY_ENABLED: z.enum(['true', 'false']).default('true'),
  EXPO_PUBLIC_SAVED_CARD_CHARGES_ENABLED: z.enum(['true', 'false']).default('false'),
  EXPO_PUBLIC_TOURS_ENABLED: z.enum(['true', 'false']).default('false'),
  EXPO_PUBLIC_SERVICES_ENABLED: z.enum(['true', 'false']).default('false'),
  EXPO_PUBLIC_PAYPAL_ENABLED: z.enum(['true', 'false']).default('false'),
  EXPO_PUBLIC_COVERAGE_STATUS: z.enum(['coming_soon', 'pilot', 'live']).default('coming_soon'),
  EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_PRIMARY: z.string().min(1).default('2261-4691'),
  EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_SECONDARY: z.string().min(1).default('2535-9191'),
  EXPO_PUBLIC_CLIENT: z.string().optional(),
  /**
   * Solo para el build QA que graba el video de App Review en un simulador de
   * EAS: ese build reporta CFBundleVersion 1 (la versión remota no se inyecta
   * en perfiles sin credenciales) y el muro lo bloquearía. Producción y
   * preview nunca lo activan.
   */
  EXPO_PUBLIC_REVIEW_DEMO: z.enum(['true', 'false']).default('false'),
})

// Vars truly required for a production build to function on Android/iOS.
// Excluded intentionally:
//   - VERIFIK_API_KEY, FACTURALLAMA_API_KEY: server-only secrets read by
//     Supabase Edge Functions (Deno.env.get(...)). Never read in client
//     code, so requiring an EXPO_PUBLIC_ prefix is wrong — they shouldn't
//     be inlined into the JS bundle at all.
const PRODUCTION_REQUIRED = [
  'EXPO_PUBLIC_SENTRY_DSN',
  'EXPO_PUBLIC_MAPBOX_TOKEN',
  'EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID',
  'EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID',
] as const

type ParsedEnv = z.infer<typeof schema>

/**
 * Placeholder env used when validation fails so module-load doesn't
 * throw. Values are intentionally INVALID so any unintended Supabase
 * (or other) call fails loudly rather than silently leaking.
 *
 * The root layout checks `env.isMisconfigured` and renders a config-
 * error screen BEFORE any provider/router that would try to use these
 * placeholders, so they should never actually hit a network.
 */
const PLACEHOLDER_ENV: ParsedEnv = {
  EXPO_PUBLIC_SUPABASE_URL: 'https://invalid.supabase.co',
  EXPO_PUBLIC_SUPABASE_ANON_KEY: 'invalid-anon-key',
  EXPO_PUBLIC_ENV: 'production',
  EXPO_PUBLIC_SENTRY_DSN: undefined,
  EXPO_PUBLIC_MAPBOX_TOKEN: undefined,
  EXPO_PUBLIC_MAPBOX_STYLE_URL: undefined,
  EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID: undefined,
  EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID: undefined,
  EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID: undefined,
  EXPO_PUBLIC_IOS_APP_STORE_URL: undefined,
  EXPO_PUBLIC_PHONE_AUTH_ENABLED: 'true',
  EXPO_PUBLIC_PRODUCT_TELEMETRY_ENABLED: 'true',
  EXPO_PUBLIC_SAVED_CARD_CHARGES_ENABLED: 'false',
  EXPO_PUBLIC_TOURS_ENABLED: 'false',
  EXPO_PUBLIC_SERVICES_ENABLED: 'false',
  EXPO_PUBLIC_PAYPAL_ENABLED: 'false',
  EXPO_PUBLIC_COVERAGE_STATUS: 'coming_soon',
  EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_PRIMARY: '2261-4691',
  EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_SECONDARY: '2535-9191',
  EXPO_PUBLIC_CLIENT: undefined,
  EXPO_PUBLIC_REVIEW_DEMO: 'false',
}

function tryParse(): { env: ParsedEnv | null; error: string | null } {
  const raw: Record<string, string | undefined> = {
    EXPO_PUBLIC_SUPABASE_URL: process.env.EXPO_PUBLIC_SUPABASE_URL,
    EXPO_PUBLIC_SUPABASE_ANON_KEY: process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY,
    EXPO_PUBLIC_ENV: process.env.EXPO_PUBLIC_ENV,
    EXPO_PUBLIC_SENTRY_DSN: process.env.EXPO_PUBLIC_SENTRY_DSN,
    EXPO_PUBLIC_MAPBOX_TOKEN: process.env.EXPO_PUBLIC_MAPBOX_TOKEN,
    EXPO_PUBLIC_MAPBOX_STYLE_URL: process.env.EXPO_PUBLIC_MAPBOX_STYLE_URL,
    EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID:
      process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID ||
      '11072298151-qc3qc04lvtgs011hba5sjer526t4qm3e.apps.googleusercontent.com',
    EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID:
      process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID ||
      '11072298151-6jrora0q4ce8qt5mqqi7f54vsa3e30en.apps.googleusercontent.com',
    EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID: process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID,
    EXPO_PUBLIC_IOS_APP_STORE_URL: process.env.EXPO_PUBLIC_IOS_APP_STORE_URL,
    EXPO_PUBLIC_PHONE_AUTH_ENABLED: process.env.EXPO_PUBLIC_PHONE_AUTH_ENABLED,
    EXPO_PUBLIC_PRODUCT_TELEMETRY_ENABLED: process.env.EXPO_PUBLIC_PRODUCT_TELEMETRY_ENABLED,
    EXPO_PUBLIC_SAVED_CARD_CHARGES_ENABLED: process.env.EXPO_PUBLIC_SAVED_CARD_CHARGES_ENABLED,
    EXPO_PUBLIC_TOURS_ENABLED: process.env.EXPO_PUBLIC_TOURS_ENABLED,
    EXPO_PUBLIC_SERVICES_ENABLED: process.env.EXPO_PUBLIC_SERVICES_ENABLED,
    EXPO_PUBLIC_PAYPAL_ENABLED: process.env.EXPO_PUBLIC_PAYPAL_ENABLED,
    EXPO_PUBLIC_COVERAGE_STATUS: process.env.EXPO_PUBLIC_COVERAGE_STATUS,
    EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_PRIMARY:
      process.env.EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_PRIMARY,
    EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_SECONDARY:
      process.env.EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_SECONDARY,
    EXPO_PUBLIC_CLIENT: process.env.EXPO_PUBLIC_CLIENT,
    EXPO_PUBLIC_REVIEW_DEMO: process.env.EXPO_PUBLIC_REVIEW_DEMO,
  }

  const result = schema.safeParse(raw)
  if (!result.success) {
    const error = result.error.issues.map((i) => `  • ${i.path.join('.')}: ${i.message}`).join('\n')
    return { env: null, error }
  }

  if (result.data.EXPO_PUBLIC_ENV === 'production') {
    const missing = PRODUCTION_REQUIRED.filter((key) => {
      const value = result.data[key]
      return !value || value.length === 0
    })
    if (missing.length > 0) {
      // Schema validated, but production-required vars are missing.
      // Return the parsed env (so isProduction etc. derive correctly)
      // alongside an error string so the root layout can decide whether
      // to render the config-error screen (yes, in production).
      return {
        env: result.data,
        error: `Missing production-required variables:\n${missing
          .map((v) => `  • ${v}`)
          .join('\n')}\nSet these in the EAS production profile or Vercel Production env.`,
      }
    }
  }

  return { env: result.data, error: null }
}

const parsed = tryParse()

// Misconfigured = either alwaysRequired schema failed (env === null) OR
// the production-required check found missing vars (error string set).
const isMisconfigured = parsed.env === null || parsed.error !== null

// Deliberately ungated: this only fires when the env is misconfigured,
// and logcat is the sole signal an engineer has while the app shows
// ConfigErrorScreen. Well-configured runs never reach this branch.
if (parsed.error) {
  console.error(`[env] Configuration issue:\n${parsed.error}`)
}

export const env: ParsedEnv & {
  isProduction: boolean
  isDev: boolean
  isPhoneAuthEnabled: boolean
  isProductTelemetryEnabled: boolean
  isSavedCardChargesEnabled: boolean
  isToursEnabled: boolean
  isServicesEnabled: boolean
  isPayPalEnabled: boolean
  coverageStatus: 'coming_soon' | 'pilot' | 'live'
  isCoverageLive: boolean
  coverageAssistancePhonePrimary: string
  coverageAssistancePhoneSecondary: string
  isReviewDemoBuild: boolean
  isMisconfigured: boolean
  configError: string | null
} = (() => {
  const effective = parsed.env ?? PLACEHOLDER_ENV
  return {
    ...effective,
    isProduction: effective.EXPO_PUBLIC_ENV === 'production',
    isDev: effective.EXPO_PUBLIC_ENV === 'development',
    isPhoneAuthEnabled: effective.EXPO_PUBLIC_PHONE_AUTH_ENABLED === 'true',
    isProductTelemetryEnabled: effective.EXPO_PUBLIC_PRODUCT_TELEMETRY_ENABLED === 'true',
    isSavedCardChargesEnabled: effective.EXPO_PUBLIC_SAVED_CARD_CHARGES_ENABLED === 'true',
    isToursEnabled: effective.EXPO_PUBLIC_TOURS_ENABLED === 'true',
    // Preview has a local-only implementation of Services, and is identified
    // by its installed app id rather than an env switch. This lets QA exercise
    // the feature without any route to production data or operations. A real
    // build stays off until its environment explicitly enables the deployed
    // database + Edge Function pair.
    isServicesEnabled: effective.EXPO_PUBLIC_SERVICES_ENABLED === 'true' || isIsolatedPreviewBuild,
    isPayPalEnabled: effective.EXPO_PUBLIC_PAYPAL_ENABLED === 'true',
    coverageStatus: effective.EXPO_PUBLIC_COVERAGE_STATUS,
    isCoverageLive: effective.EXPO_PUBLIC_COVERAGE_STATUS === 'live',
    coverageAssistancePhonePrimary: effective.EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_PRIMARY,
    coverageAssistancePhoneSecondary: effective.EXPO_PUBLIC_COVERAGE_ASSISTANCE_PHONE_SECONDARY,
    isReviewDemoBuild: effective.EXPO_PUBLIC_REVIEW_DEMO === 'true',
    isMisconfigured,
    configError: parsed.error,
  }
})()
